import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Mail } from 'lucide-react';
import Reveal from '../components/Reveal';

// Company privacy policy for International Quality Group Co. Hosted here so the
// company's app listings and its Meta / WhatsApp Business Platform app can point
// at a real, resolving privacy policy on the company's own domain. Written in
// English and kept factual — describe only what the company and its products do.

const UPDATED = 'Last updated: September 2026';

const SECTIONS = [
  {
    title: 'Who we are',
    body:
      'International Quality Group Co. ("IQG", "we", "us") is a Kuwait-based company providing ' +
      'logistics and technology services, including business software such as point-of-sale and ' +
      'ordering platforms and related customer-messaging services. This policy explains what ' +
      'information we handle and how we protect it across our website, apps, and business messaging.',
  },
  {
    title: 'Information we collect',
    body:
      'Depending on the service, we may process: contact and account details (name, phone number, ' +
      'email) that you or a business provides; order, payment-method, and transaction records needed ' +
      'to operate a point of sale or fulfil a request; enquiry details you submit through our contact ' +
      'forms; and basic technical data such as device type and app version used for reliability and ' +
      'security. We collect only what a feature needs to work.',
  },
  {
    title: 'WhatsApp and SMS messaging',
    body:
      'Where a business enables it, we send transactional messages — such as one-time verification ' +
      'codes (OTP), order confirmations, and receipts — over WhatsApp (via the WhatsApp Business ' +
      'Platform) or SMS, to the phone number provided for that purpose. We use the number only to ' +
      'deliver those messages and the delivery/read status confirming they arrived. We do not sell ' +
      'phone numbers or send unrelated marketing without consent.',
  },
  {
    title: 'How we use information',
    body:
      'To provide and operate our services, authenticate users, process and record orders and ' +
      'payments, respond to enquiries, deliver the messages described above, prevent fraud and abuse, ' +
      'and meet legal and accounting obligations. We do not use this data to build advertising profiles.',
  },
  {
    title: 'Sharing',
    body:
      'We share data only with the processors needed to run a service — for example Meta (WhatsApp ' +
      'Business Platform) and SMS gateways to deliver messages, and payment providers to process ' +
      'transactions — each acting under their own terms. We disclose information when required by law. ' +
      'We do not sell personal data.',
  },
  {
    title: 'Retention and security',
    body:
      'We keep information only as long as needed to provide a service and satisfy legal or accounting ' +
      'requirements, then delete or anonymize it. Data is protected with encryption in transit, access ' +
      'controls, and network isolation; messaging credentials are stored as secrets, never in plain text.',
  },
  {
    title: 'Your rights',
    body:
      'You may request access to, correction of, or deletion of the personal data we hold about you, ' +
      'subject to legal retention limits. For business-operated products, we act on the business’s ' +
      'instruction as the data controller. Contact us and we will help route your request.',
  },
  {
    title: 'Changes',
    body:
      'We may update this policy as our services evolve. Material changes will be reflected here with a ' +
      'new “last updated” date.',
  },
];

const Privacy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/10 mb-6">
              <Shield className="w-7 h-7" aria-hidden="true" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">Privacy Policy</h1>
            <p className="mt-4 text-lg text-slate-300">
              How International Quality Group handles your information across our website, apps, and business messaging.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm text-slate-400">{UPDATED}</p>

          <div className="mt-8 space-y-5">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.title} delay={Math.min(i, 5) * 60}>
                <Card className="border-slate-200">
                  <CardContent className="p-6">
                    <h2 className="text-xl font-semibold text-slate-900">{s.title}</h2>
                    <p className="mt-3 leading-relaxed text-slate-600">{s.body}</p>
                  </CardContent>
                </Card>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <Card className="mt-8 border-slate-200">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-slate-900">Contact</h2>
                <p className="mt-3 text-slate-600">Questions about this policy or your data? Reach us at:</p>
                <a
                  href="mailto:info@intqualitygroup.com"
                  className="mt-4 inline-flex items-center gap-2 font-medium text-slate-900 hover:underline"
                  dir="ltr"
                >
                  <Mail className="w-4 h-4" aria-hidden="true" />
                  info@intqualitygroup.com
                </a>
              </CardContent>
            </Card>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
